import React, { useState } from "react";
import { useTranslate, useCreate, useUpdate, useInvalidate } from "@refinedev/core";
import { List, useTable, DeleteButton } from "@refinedev/antd";
import { Table, Space, Button, Tag, Modal, Form, Input, InputNumber, Switch, Typography } from "antd";
import { GlassCard } from "../../shared/components";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ICreditPack } from "../../models/credit-pack";

export const CreditPackList: React.FC<{ hideHeader?: boolean }> = ({ hideHeader }) => {
    const _t = useTranslate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<ICreditPack | null>(null);
    const [form] = Form.useForm();
    const invalidate = useInvalidate();

    const { tableProps, tableQueryResult } = useTable<ICreditPack>({
        resource: "credit-packs",
        syncWithLocation: true,
    });

    const { Text } = Typography;

    const { mutate: createPack, isLoading: isCreating } = useCreate();
    const { mutate: updatePack, isLoading: isUpdating } = useUpdate();

    const openCreateModal = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true });
        setIsModalOpen(true);
    };

    const openEditModal = (record: ICreditPack) => {
        setEditingRecord(record);
        form.setFieldsValue({
            name: record.name,
            priceExclTax: record.priceExclTax / 100,
            credits: record.credits,
            description: record.description,
            isActive: record.isActive,
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingRecord(null);
        form.resetFields();
    };

    const handleFormFinish = (values: any) => {
        const payload = {
            ...values,
            priceExclTax: Math.round(values.priceExclTax * 100),
        };

        if (editingRecord) {
            updatePack(
                { resource: "credit-packs", id: editingRecord.id, values: payload },
                {
                    onSuccess: () => {
                        closeModal();
                        invalidate({ resource: "credit-packs", invalidates: ["list"] });
                        tableQueryResult?.refetch();
                    },
                },
            );
        } else {
            createPack(
                { resource: "credit-packs", values: payload },
                {
                    onSuccess: () => {
                        closeModal();
                        invalidate({ resource: "credit-packs", invalidates: ["list"] });
                        tableQueryResult?.refetch();
                    },
                },
            );
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <List
                resource="credit-packs"
                title={hideHeader ? false : _t("credit-packs.titles.list", "Credit Packs")}
                breadcrumb={hideHeader ? false : undefined}
                headerButtons={
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                        {_t("buttons.create", "Create")}
                    </Button>
                }
            >
                <style>{`
                    .credit-packs-list-table-view .ant-table { background: transparent !important; }
                    .credit-packs-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                    .credit-packs-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
                `}</style>
                <GlassCard className="credit-packs-list-table-view !p-0 overflow-hidden">
                <Table {...tableProps} rowKey="id">
                    <Table.Column
                        dataIndex="name"
                        title={_t("credit-packs.fields.name", "Name")}
                        render={(name: string, record: ICreditPack) => (
                            <Space direction="vertical" size={0}>
                                <Text strong>{name}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    #{record.id}
                                </Text>
                            </Space>
                        )}
                    />
                    <Table.Column
                        dataIndex="description"
                        title={_t("credit-packs.fields.description", "Description")}
                    />
                    <Table.Column
                        dataIndex="priceExclTax"
                        title={_t("credit-packs.fields.priceExclTax", "Price excl. tax")}
                        render={(value: number) =>
                            `${(value / 100).toFixed(2)} €`
                        }
                    />
                    <Table.Column
                        dataIndex="credits"
                        title={_t("credit-packs.fields.credits", "Credits")}
                        render={(value: number) => (
                            <Tag color="blue">{value}</Tag>
                        )}
                    />
                    <Table.Column
                        dataIndex="isActive"
                        title={_t("credit-packs.fields.isActive", "Active")}
                        render={(value: boolean) => (
                            <Tag color={value ? "green" : "red"}>
                                {value ? _t("titles.yes", "Yes") : _t("titles.no", "No")}
                            </Tag>
                        )}
                    />
                    <Table.Column
                        title={_t("table.actions", "Actions")}
                        dataIndex="actions"
                        render={(_, record: ICreditPack) => (
                            <Space>
                                <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => openEditModal(record)}
                                />
                                <DeleteButton
                                    resource="credit-packs"
                                    hideText
                                    size="small"
                                    recordItemId={record.id}
                                    onSuccess={() => {
                                        tableQueryResult?.refetch();
                                    }}
                                />
                            </Space>
                        )}
                    />
                </Table>
                </GlassCard>
            </List>

            <Modal
                title={
                    editingRecord
                        ? _t("credit-packs.titles.edit", "Edit Credit Pack")
                        : _t("credit-packs.titles.create", "Create Credit Pack")
                }
                open={isModalOpen}
                onCancel={closeModal}
                footer={[
                    <Button key="cancel" onClick={closeModal}>
                        {_t("buttons.cancel", "Cancel")}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={isCreating || isUpdating}
                        onClick={() => form.submit()}
                    >
                        {_t("buttons.save", "Save")}
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFormFinish}
                >
                    <Form.Item
                        label={_t("credit-packs.fields.name", "Name")}
                        name="name"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={_t("credit-packs.fields.priceExclTax", "Price excl. tax (€)")}
                        name="priceExclTax"
                        rules={[{ required: true }]}
                    >
                        <InputNumber className="w-full" min={0} step={0.01} precision={2} addonAfter="€" />
                    </Form.Item>
                    <Form.Item
                        label={_t("credit-packs.fields.credits", "Credits")}
                        name="credits"
                        rules={[{ required: true }]}
                    >
                        <InputNumber className="w-full" min={0} precision={0} />
                    </Form.Item>
                    <Form.Item
                        label={_t("credit-packs.fields.description", "Description")}
                        name="description"
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        label={_t("credit-packs.fields.isActive", "Active")}
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
